import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Collapse, Loader } from '@mantine/core';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { newCOLORS } from '../../../../styles/colors';
import { Text } from '../../../../ui-library/typography/typography';
import type { SubagentInfo } from '../../flora.type';

interface AgentSubagentSectionProps {
  subagent: SubagentInfo;
  children: ReactNode;
}

// Friendly display labels for known backend subagent types. The backend agent
// type stays `general-purpose` (the LLM-facing tool contract); this only
// relabels it in the UI. Unknown names fall back to title-casing the slug.
const SUBAGENT_DISPLAY_NAMES: Record<string, string> = {
  'general-purpose': 'Flora Subagent',
};

function formatSubagentName(name: string): string {
  const mapped = SUBAGENT_DISPLAY_NAMES[name];
  if (mapped) return mapped;
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function AgentSubagentSection({ subagent, children }: AgentSubagentSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isError = subagent.status === 'error';
  const isPending = subagent.status === 'in_progress';
  const isCompleted = subagent.status === 'completed';

  return (
    <Container $isError={isError}>
      <Header onClick={() => setIsExpanded(!isExpanded)}>
        <HeaderLeft>
          {isPending ? (
            <Loader size={14} color="var(--secondary-base)" />
          ) : isError ? (
            <Icon icon="material-symbols:error-outline" width={16} height={16} style={{ color: newCOLORS.red }} />
          ) : (
            <Icon
              icon="material-symbols:check-circle-outline"
              width={16}
              height={16}
              style={{ color: 'var(--primary-base)' }}
            />
          )}
          <Icon
            icon="material-symbols:account-tree-outline"
            width={15}
            height={15}
            style={{ color: 'var(--secondary-base)' }}
          />
          <Text size="small" weight="bold" color={isError ? newCOLORS.red : 'var(--secondary-darkest)'}>
            {formatSubagentName(subagent.name)}
          </Text>
          {isPending && <StatusBadge $variant="pending">Running</StatusBadge>}
          {isCompleted && <StatusBadge $variant="completed">Done</StatusBadge>}
          {isError && <StatusBadge $variant="error">Error</StatusBadge>}
        </HeaderLeft>
        <Icon
          icon={isExpanded ? 'material-symbols:expand-less' : 'material-symbols:expand-more'}
          width={18}
          height={18}
          style={{ color: 'var(--ink-lighter)' }}
        />
      </Header>
      {subagent.description && (
        <DescriptionWrap title={subagent.description}>
          <DescriptionText size="small" color="var(--ink-lighter)">
            {subagent.description}
          </DescriptionText>
        </DescriptionWrap>
      )}
      <Collapse expanded={isExpanded}>
        <Body>{children}</Body>
      </Collapse>
    </Container>
  );
}

const Container = styled.div<{ $isError?: boolean }>`
  margin: 8px 0;
  border: 1.5px solid ${({ $isError }) => ($isError ? 'var(--red-lighter)' : 'var(--secondary-lighter)')};
  border-radius: 12px;
  background: ${({ $isError }) => ($isError ? 'var(--flora-error-surface)' : 'var(--secondary-lightest)')};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--flora-subagent-hover);
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

const StatusBadge = styled.span<{ $variant: 'pending' | 'completed' | 'error' }>`
  font-size: 11px;
  font-weight: 500;
  padding: 1px 7px;
  border-radius: 4px;
  flex-shrink: 0;
  ${({ $variant }) => {
    if ($variant === 'pending') return `color: var(--secondary-darkest); background: var(--flora-subagent-badge-bg);`;
    if ($variant === 'completed') return `color: var(--primary-darkest); background: var(--primary-lightest);`;
    return `color: ${newCOLORS.red}; background: var(--red-lightest);`;
  }}
`;

const DescriptionWrap = styled.div`
  padding: 6px 14px 8px;
  border-bottom: 1px solid var(--flora-subagent-border-accent);
  background: var(--flora-subagent-surface);
`;

// The description is the full delegated-task prompt, which can run several
// sentences. Clamp it to two lines in the header (full text on hover via the
// wrapper's title attribute) so the section stays scannable.
const DescriptionText = styled(Text)`
  font-style: italic;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Body = styled.div`
  padding: 10px 14px;
  background: var(--flora-subagent-body);
`;
